// ============================================================================
// prefs.ts — небольшие SSoT-хелперы экрана голосовых комнат (клиент):
// подписи/порядок ролей участников и ЛИЧНАЯ настройка «вхожу с выключенным
// микрофоном» (localStorage, на устройстве; сервер хранит muted_self на входе).
// ============================================================================

import type { VoiceRoomMember } from '@zdc/sdk';
import { logSwallowed } from '@/lib/errors';

/** Роли участника комнаты (сервер: moderator | speaker | listener). */
export const VOICE_ROLE_LABELS: Record<string, string> = {
  moderator: 'Ведущий',
  speaker: 'Говорящий',
  listener: 'Слушающий',
};

/** Порядок ролей в списке участников (меньше — выше). */
export const VOICE_ROLE_ORDER: Record<string, number> = {
  moderator: 0,
  speaker: 1,
  listener: 2,
};

/** Отображаемое имя участника (себя помечаем «(вы)»). */
export function memberLabel(m: VoiceRoomMember, myId: string): string {
  const name = m.entityName && m.entityName.trim() ? m.entityName : m.entityId;
  return m.entityId === myId ? `${name} (вы)` : name;
}

/** Порядок участников: в комнате — выше; создатель первый; по роли; затем по имени. */
export function compareMembers(a: VoiceRoomMember, b: VoiceRoomMember, creatorId: string): number {
  const aActive = a.state === 'active' ? 0 : 1;
  const bActive = b.state === 'active' ? 0 : 1;
  if (aActive !== bActive) return aActive - bActive;
  if (a.entityId === creatorId) return -1;
  if (b.entityId === creatorId) return 1;
  const roleDiff = (VOICE_ROLE_ORDER[a.role] ?? 9) - (VOICE_ROLE_ORDER[b.role] ?? 9);
  if (roleDiff !== 0) return roleDiff;
  return memberLabel(a, '').localeCompare(memberLabel(b, ''));
}

const MUTED_PREF_KEY = 'hydra_voice_room_muted';

/** Личная настройка: входить в комнату с выключенным микрофоном. */
export function readMutedPref(): boolean {
  try {
    return localStorage.getItem(MUTED_PREF_KEY) === '1';
  } catch (e) {
    logSwallowed(e, 'Голосовые комнаты: чтение настройки микрофона');
    return false;
  }
}

/** Сохранить личную настройку микрофона. */
export function writeMutedPref(muted: boolean): void {
  try {
    localStorage.setItem(MUTED_PREF_KEY, muted ? '1' : '0');
  } catch (e) {
    logSwallowed(e, 'Голосовые комнаты: сохранение настройки микрофона');
  }
}
